import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { AnchorButton, Button, Callout } from "@blueprintjs/core";
import {
  faCheckCircle,
  faPlug,
  faRobot,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useBasePath, useScans } from "@hooks";
import { MINUTE } from "@utils";

import { CIProvider } from "../../types";
import { ErrorText } from "../SetupStyledComponents";

import { StepItem } from "./StepItem";

const FIVE_MINUTES = MINUTE * 5;

const StyledCallout = styled(Callout)`
  display: flex;
  font-size: var(--global-font-size-s);
  color: var(--dark-gray1);
`;

const SuccessTitle = styled.div`
  color: var(--button-indigo);
  font-weight: var(--global-font-weight-bold);
  font-size: var(--global-font-size-m);
`;

const Buttons = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 20px;
`;

const SuccessDescription = () => {
  const basePath = useBasePath();

  return (
    <StyledCallout intent="primary" icon={null}>
      <FontAwesomeIcon
        icon={faRobot}
        color="var(--button-indigo)"
        style={{ padding: "4px 8px 0 0" }}
      />
      <div>
        <SuccessTitle>It's alive!</SuccessTitle>
        Semgrep will regularly check your code and deliver findings.
        <Buttons>
          <AnchorButton intent="primary" href={`${basePath}/findings`}>
            See findings
          </AnchorButton>
          <AnchorButton href={`${basePath}/settings/members`}>
            Invite team members
          </AnchorButton>
        </Buttons>
      </div>
    </StyledCallout>
  );
};
const ErrorDescription = () => {
  const basePath = useBasePath();

  return (
    <>
      <ErrorText>
        Couldn't find any recently started scans. Double check your config file
        and secret and check the connection again.
      </ErrorText>
      <ErrorText>
        If you keep receiving this error,{" "}
        <Link to={`${basePath}/support`}>let us know</Link>.
      </ErrorText>
    </>
  );
};

export const StepThreeScan: React.FC<{ ciProvider: CIProvider }> = ({
  ciProvider,
}) => {
  // setting search params to look only at scans in the last five minutes
  const searchParams = new URLSearchParams();
  const fiveMinAgo = Date.now() - FIVE_MINUTES;
  searchParams.set("since", (fiveMinAgo / 1000).toString());
  const { refetch } = useScans(false, searchParams);
  const [isChecking, setIsChecking] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const connectionMessageRef = useRef<null | HTMLDivElement>(null);

  const scrollToConnectionMessage = () => {
    if (connectionMessageRef?.current !== null) {
      connectionMessageRef.current.scrollIntoView();
    }
  };

  const checkConnection = () => {
    setIsChecking(true);
    setIsSuccess(false);
    setIsError(false);
    refetch()
      .then((res) => {
        if (res.data === undefined || res.data.length === 0) {
          setIsError(true);
        } else {
          setIsSuccess(true);
        }
        setIsChecking(false);
      })
      .then(scrollToConnectionMessage);
  };

  let scanMessage = "all files";
  if (ciProvider === "github") {
    scanMessage = "changed files in PRs";
  } else if (ciProvider === "gitlab") {
    scanMessage = "changed files in MRs";
  }

  return (
    <StepItem
      number={3}
      title="Check connection"
      description={
        <>
          <div>Now let's make sure that everything was set up properly.</div>
          <div>
            The first scan checks everything in the project. After, by default,
            scans apply to {scanMessage} and only report new findings.
          </div>
        </>
      }
      button={
        <Button
          onClick={checkConnection}
          loading={isChecking}
          disabled={isSuccess}
          icon={<FontAwesomeIcon icon={isSuccess ? faCheckCircle : faPlug} />}
        >
          {isSuccess ? "Connected!" : "Check connection"}
        </Button>
      }
      bottomDescription={
        isError ? (
          <div ref={connectionMessageRef}>
            <ErrorDescription />
          </div>
        ) : isSuccess ? (
          <div ref={connectionMessageRef}>
            <SuccessDescription />
          </div>
        ) : undefined
      }
    />
  );
};
